import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Api, urlWithAccessToken, downloadFile } from '../../api/Assinafy'
import { Table, Status, Image, Button, Icon, Dropdown, Tooltip, Spinner, Toast, Modal } from '../../components'
import { statusId, statusTitle } from '../../utils/Documents';
import { dateFormat } from '../../utils/Formats';

import thumbDocument from '../../assets/images/thumb-document.svg';

const DocumentsList = (props) => {
  const [toast, setToast] = useState('')
  const { state } = useLocation()

  useEffect(() => {
    if (state?.toast) {
      setToast(state.toast)
      window.history.replaceState({}, document.title)
    }
  }, [state])

  return (<>
    <Table {...props}
      className="table-bordered table-hover"
      header={[
        { name: 'Nome' },
        { name: 'Status' },
        { name: 'Alteração' },
        { name: 'Validade' },
        { name: '' }
      ]}
      renderRow={(row, i) => <TableItem key={i} {...row}
        setToast={setToast}
      />}
    />
    <Toast show={toast} onClose={() => setToast('')}>
      {toast}
    </Toast>
  </>)
}

const TableItem = ({ setToast, ...props }) => {
  const [tooltipShow, setTooltipShow] = useState(false)
  const { assignment } = props
  const summary = assignment?.summary

  const tooltipMouseEnter = () => {
    setTooltipShow(true)
  }

  const tooltipMouseLeave = () => {
    setTooltipShow(false)
  }

  return (
    <tr className={`document-status-${statusId(props.status)}`}>
      <td>
        <Link
          className="document-header"
          onMouseLeave={tooltipMouseLeave}
          to={`/dashboard/documents/${props.id}/view`}
        >
          <Image
            width="40"
            height="56"
            src={props.artifacts?.thumbnail && urlWithAccessToken(props.artifacts.thumbnail)}
            placeholder={thumbDocument}
          />
          <div className="document-title">{props.name}</div>
          <div className="document-signer d-lg-none">
            <Status id={props.status} />
            {summary ? `(${summary.completed_count}/${summary.signer_count})` : ''}
          </div>
          <div className="document-signer d-none d-lg-block" onMouseEnter={tooltipMouseEnter}>
            {summary ?
              <>
                {summary.completed_count === summary.signer_count ?
                  'Assinaturas completas'
                  :
                  `${summary.completed_count}/${summary.signer_count} assinaturas`
                }
                <Tooltip show={tooltipShow}>
                  {summary.signers.map((signer, i) => {
                    return <div key={i}>
                      <Icon
                        id={`icon-${signer.completed ? 'check' : 'close'}`}
                        size="12"
                      /> - {signer.full_name}
                    </div>
                  })}
                </Tooltip>
              </>
              :
              statusTitle(props.status)
            }
          </div>
        </Link>
      </td>
      <td className="d-none d-sm-table-cell">
        <Status id={props.status} />
      </td>
      <td className="d-none d-lg-table-cell">
        {dateFormat(props.updated_at)}
      </td>
      <td className="d-none d-lg-table-cell">
        {props.assignment?.expires_at ?
          dateFormat(props.assignment.expires_at)
          :
          '-'
        }
      </td>
      <td className="col-action">
        <TableActions
          document={props}
          setToast={setToast}
        />
      </td>
    </tr>
  )
}

const TableActions = ({ document, setToast }) => {
  const documentDelete = Api('document/delete')
  const { id: documentId, name: documentName, status: documentStatus, artifacts, assignment } = document
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [modalRemove, setModalRemove] = useState(false)
  const dropdownRef = useRef()

  const handleResendAssignmentAll = (i) => {
    const signers = assignment.summary.signers.filter((s) => !s.completed)

    setLoading(i)

    Promise.all(signers.map(async (signer) => {
      await Api('assignment/resend')(documentId, assignment.id, signer.id)
        .catch(() => {
          setLoading(false)
          dropdownRef.current.close()
        })
    }))
      .then(() => {
        setLoading(false)
        dropdownRef.current.close()
        setToast(`Documento ${documentName} reenviado com sucesso`)
      })
  }

  const downloadDocument = (i, type) => {
    setLoading(i)

    downloadFile(urlWithAccessToken(artifacts[type]))
      .then(() => {
        setLoading(false)
        dropdownRef.current.close()
      })
  }

  const handleDelete = (i) => {
    setLoading(true)

    documentDelete(documentId)
      .then(({ status, data: { data } }) => {
        if (status === 200) {
          setLoading(false)
          setModalRemove(false)
          navigate(0, {
            state: { toast: `Documento ${documentName} removido com sucesso` }
          })
        }
      })
      .catch(({ response }) => {
        setLoading(false)
        setModalRemove(false)
      })
  }

  const actionsButtons = [
    {
      id: 'view',
      icon: 'icon-visibility',
      text: 'VISUALIZAR',
      onClick: () => navigate(`/dashboard/documents/${documentId}/view`)
    },
    {
      id: 'edit',
      icon: 'icon-edit',
      text: 'EDITAR',
      onClick: () => navigate(`/dashboard/documents/${documentId}/view`)
    },
    Api('assignment/resend') && {
      id: 'resend',
      icon: 'icon-share',
      text: 'REENVIAR',
      loadingText: <>
        <Spinner className="me-1" />
        ENVIANDO
      </>,
      onClick: handleResendAssignmentAll
    },
    downloadFile && {
      id: 'download',
      icon: 'icon-arrow_downward',
      text: 'DOWNLOAD ORIGINAL',
      onClick: (i) => downloadDocument(i, 'original')
    },
    {
      id: 'download',
      icon: 'icon-task_line',
      text: 'DOWNLOAD ASSINADO',
      onClick: (i) => downloadDocument(i, 'certificated')
    },
    documentDelete && {
      icon: 'icon-trash',
      text: 'EXCLUIR',
      onClick: () => setModalRemove(true)
    }
  ]

  const primaryByStatus = (status) => {
    switch (status) {
      case 'certificated':
        return { ...actionsButtons[4], text: 'DOWNLOAD' }
      case 'rejected_by_signer':
        return { ...actionsButtons[3], text: 'DOWNLOAD' }
      case 'rejected_by_user':
        return actionsButtons[2]
      case 'metadata_ready':
        return actionsButtons[1]
      case 'failed':
        return actionsButtons[1]
      default:
        return actionsButtons[0]
    }
  }

  const actionsByStatus = (status) => {
    switch (status) {
      case 'metadata_ready':
        return [actionsButtons[0], actionsButtons[1], actionsButtons[3], actionsButtons[5]]
      case 'pending_signature':
        return [actionsButtons[0], actionsButtons[2], actionsButtons[3], actionsButtons[5]]
      case 'rejected_by_user': case 'rejected_by_signer':
        return [actionsButtons[0], actionsButtons[3], actionsButtons[5]]
      case 'expired':
        return [actionsButtons[0], actionsButtons[3], actionsButtons[5]]
      case 'certificated':
        return [actionsButtons[0], actionsButtons[3], actionsButtons[4], actionsButtons[5]]
      case 'certificating':
        return [actionsButtons[0], actionsButtons[3], actionsButtons[5]]
      default:
        return [actionsButtons[3], actionsButtons[5]]
    }
  }

  const { id, icon, text, loadingText, onClick } = primaryByStatus(documentStatus)

  return (<>
    <div className="table-actions">
      <div className="d-none d-xl-table-cell">
        <Button variant="light"
          className="has-hover"
          as={Button}
          onClick={() => onClick(id)}
          loading={loading === id}
          loadingText={loadingText}
          disabled={statusId(document.status) === 'processing' && true}
        >
          <Icon id={icon} size="18" className="me-1" />
          {text}
        </Button>
      </div>
      <Dropdown
        ref={dropdownRef}
        align="right"
        variant="action"
        button={<Icon id="icon-more_vert" />}
      >
        {actionsByStatus(documentStatus).map(({ id, icon, text, loadingText, onClick }, i) =>
          <Dropdown.Item key={i}
            as={Button}
            onClick={() => onClick(id)}
            loading={loading === id}
            loadingText={loadingText}
          >
            <Icon id={icon} className="me-1" size="20" />
            {text}
          </Dropdown.Item>
        )}
      </Dropdown>
    </div>
    <Modal.Alert show={!!modalRemove}
      align="center"
      variant="danger"
      icon="icon-trash"
      title="Deseja realmente remover esse documento?"
      onClose={() => setModalRemove(false)}
    >
      <Modal.Footer>
        <Button variant="danger" submit
          onClick={handleDelete}
          loading={loading}
        >
          <Icon id="icon-trash" className="me-1" size="16" />
          REMOVER
        </Button>
        <Button variant="info" submit
          onClick={() => setModalRemove(false)}
        >
          NÃO, FECHAR
        </Button>
      </Modal.Footer>
    </Modal.Alert>
  </>)
}

export default DocumentsList;